import React from 'react';
import { fireEvent, render, screen } from '@testing-library/react';

import ConfigProvider from 'components/ConfigProvider';

import AccessModal from './AccessModal';

const USER = {
  id: 7,
  name: 'John Martinez',
  email: 'jmartinez@email.com',
  jobRole: 'Fleet Manager',
};

const OPTIONS = [
  { label: 'Account Name A', value: 1 },
  { label: 'Account Name B', value: 2 },
];

const renderModal = (props = {}) =>
  render(
    <ConfigProvider>
      <AccessModal
        accountOptions={OPTIONS}
        action="remove"
        onChangeSelected={jest.fn()}
        onClose={jest.fn()}
        onSubmit={jest.fn()}
        selectedIds={[1, 2]}
        user={USER}
        visible
        {...props}
      />
    </ConfigProvider>,
  );

const submitButton = (name) => screen.getAllByRole('button', { name })[0];

describe('management/Users AccessModal', () => {
  it('renders nothing when not visible', () => {
    renderModal({ visible: false });

    expect(screen.queryByText('Remove Access')).not.toBeInTheDocument();
  });

  describe('the contact card', () => {
    it('names the user and their job role', () => {
      renderModal();

      expect(
        screen.getByText('John Martinez (Fleet Manager)'),
      ).toBeInTheDocument();
    });

    // The design's card shows "Name (<Job Role>)"; a user with no job role
    // would otherwise render an empty pair of brackets.
    it('drops the brackets when there is no job role', () => {
      renderModal({ user: { ...USER, jobRole: null } });

      expect(screen.getByText('John Martinez')).toBeInTheDocument();
    });

    it('shows the email', () => {
      renderModal();

      expect(screen.getByText('jmartinez@email.com')).toBeInTheDocument();
    });
  });

  describe('removing', () => {
    it('titles itself Remove Access', () => {
      renderModal({ action: 'remove' });

      expect(submitButton('Remove Access')).toBeInTheDocument();
    });

    it('says what clicking it will do', () => {
      renderModal({ action: 'remove' });

      expect(
        screen.getByText(
          'Clicking REMOVE ACCESS will prevent this user from accessing the selected accounts.',
        ),
      ).toBeInTheDocument();
    });

    it('shows Removing... while submitting', () => {
      renderModal({ action: 'remove', isSubmitting: true });

      expect(screen.getByText('Removing...')).toBeInTheDocument();
    });
  });

  describe('restoring', () => {
    it('titles itself Restore Access', () => {
      renderModal({ action: 'restore' });

      expect(submitButton('Restore Access')).toBeInTheDocument();
    });

    it('says what clicking it will do', () => {
      renderModal({ action: 'restore' });

      expect(
        screen.getByText(
          'Clicking RESTORE ACCESS will again grant this user access to the selected accounts with previous permissions.',
        ),
      ).toBeInTheDocument();
    });
  });

  describe('the submit button', () => {
    it('is enabled with accounts selected', () => {
      renderModal();

      expect(submitButton('Remove Access')).not.toBeDisabled();
    });

    // Submitting none would be a request the server rejects, so the modal says
    // no first.
    it('is disabled when the selection is empty', () => {
      renderModal({ selectedIds: [] });

      expect(submitButton('Remove Access')).toBeDisabled();
    });

    it('is disabled while the accounts are still loading', () => {
      renderModal({ isLoading: true });

      expect(submitButton('Remove Access')).toBeDisabled();
    });

    it('calls onSubmit', () => {
      const onSubmit = jest.fn();
      renderModal({ onSubmit });

      fireEvent.click(submitButton('Remove Access'));

      expect(onSubmit).toHaveBeenCalled();
    });
  });

  it('cancels via the secondary action', () => {
    const onClose = jest.fn();
    renderModal({ onClose });

    fireEvent.click(screen.getByRole('button', { name: 'Cancel' }));

    expect(onClose).toHaveBeenCalled();
  });

  describe('while the accounts are loading', () => {
    it('says so rather than showing an empty picker', () => {
      renderModal({ accountOptions: [], isLoading: true });

      expect(screen.getByText('Loading accounts...')).toBeInTheDocument();
    });
  });

  describe('when the user has no accounts to act on', () => {
    it('explains rather than showing an empty picker', () => {
      renderModal({ accountOptions: [], selectedIds: [] });

      expect(
        screen.getByText('This user has no accounts to remove.'),
      ).toBeInTheDocument();
    });

    it('words it for the restore case', () => {
      renderModal({ accountOptions: [], action: 'restore', selectedIds: [] });

      expect(
        screen.getByText('This user has no removed accounts to restore.'),
      ).toBeInTheDocument();
    });
  });

  // The design keeps the dialog open on failure so the admin can narrow the
  // selection and retry without rebuilding it.
  describe('a failure', () => {
    it('shows inline', () => {
      renderModal({ error: 'Something went wrong. Please try again.' });

      expect(screen.getByRole('alert')).toHaveTextContent(
        'Something went wrong. Please try again.',
      );
    });

    it('is titled for the remove case', () => {
      renderModal({ action: 'remove', error: 'boom' });

      expect(screen.getByRole('alert')).toHaveTextContent(
        'Access not removed.',
      );
    });

    it('is titled for the restore case', () => {
      renderModal({ action: 'restore', error: 'boom' });

      expect(screen.getByRole('alert')).toHaveTextContent(
        'Access not restored.',
      );
    });

    it('leaves the dialog usable', () => {
      renderModal({ error: 'boom' });

      expect(submitButton('Remove Access')).not.toBeDisabled();
      expect(screen.getByRole('button', { name: 'Cancel' })).not.toBeDisabled();
    });
  });

  it('shows no error region when there is none', () => {
    renderModal();

    expect(screen.queryByRole('alert')).not.toBeInTheDocument();
  });

  // The design gives each account tag an x (Figma 1301:21873); without it the
  // only way to drop one is through the panel.
  describe('the account tags', () => {
    it('can be dismissed one at a time', () => {
      renderModal();

      expect(
        screen.getByRole('button', { name: 'Remove Account Name A' }),
      ).toBeInTheDocument();
    });

    it('drops just that account from the selection', () => {
      const onChangeSelected = jest.fn();
      renderModal({ onChangeSelected });

      fireEvent.click(
        screen.getByRole('button', { name: 'Remove Account Name A' }),
      );

      expect(onChangeSelected).toHaveBeenCalledWith([2]);
    });
  });

  // The component stays mounted between openings, so a term typed into the
  // picker would otherwise still be filtering it the next time it opens, and
  // against a different user's accounts.
  describe('reopening after a search', () => {
    const openWith = (props) =>
      render(
        <ConfigProvider>
          <AccessModal
            accountOptions={OPTIONS}
            action="remove"
            onChangeSelected={jest.fn()}
            onClose={jest.fn()}
            onSubmit={jest.fn()}
            selectedIds={[]}
            user={USER}
            {...props}
          />
        </ConfigProvider>,
      );

    const search = (term) =>
      fireEvent.change(screen.getByRole('combobox'), {
        target: { value: term },
      });

    it('offers every account again', () => {
      const { rerender } = openWith({ visible: true });

      search('Name A');
      expect(screen.queryByText('Account Name B')).not.toBeInTheDocument();

      const reopen = (visible) =>
        rerender(
          <ConfigProvider>
            <AccessModal
              accountOptions={OPTIONS}
              action="remove"
              onChangeSelected={jest.fn()}
              onClose={jest.fn()}
              onSubmit={jest.fn()}
              selectedIds={[]}
              user={USER}
              visible={visible}
            />
          </ConfigProvider>,
        );

      reopen(false);
      reopen(true);

      fireEvent.click(
        screen.getByRole('button', { name: 'Show account options' }),
      );

      expect(screen.getByText('Account Name B')).toBeInTheDocument();
    });
  });
});
